import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";

import { describeTimeRanges, formatBirthDate } from "/imports/ui/components/preferences";

function PreferenceChips({ items, getLabel }) {
  if (!items?.length) {
    return (
      <Typography variant="body1" color="textSecondary">
        Not set
      </Typography>
    );
  }
  return (
    <Box display="flex" gap={0.75} flexWrap="wrap">
      {items.map((it, i) => (
        <Chip key={i} size="small" color="primary" label={getLabel(it)} variant="outlined" sx={{ fontSize: "0.875rem", height: "1.75rem" }} />
      ))}
    </Box>
  );
}

function PreferenceSection({ label, children }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.8rem", display: "block", mb: 0.75 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function PreferencesSummary({ data, onEdit }) {
  const timeLines = describeTimeRanges(data.timeRanges);
  const dob = formatBirthDate(data.birthDate);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
        <Typography variant="body2" color="textSecondary">
          Tell us what you like so your club can tailor recommendations to you.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon sx={{ fontSize: "0.875rem" }} />}
          onClick={onEdit}
          sx={{ textTransform: "none", flexShrink: 0, py: 0.25, px: 1, fontSize: "0.75rem" }}
        >
          Edit
        </Button>
      </Box>

      <Box display="flex" flexDirection="column" gap={2.5}>
        <PreferenceSection label="Preferred sports">
          <PreferenceChips items={data.sports} getLabel={(s) => s.sportName} />
        </PreferenceSection>
        <PreferenceSection label="Preferred services">
          <PreferenceChips items={data.services} getLabel={(s) => s.serviceName} />
        </PreferenceSection>
        <PreferenceSection label="Preferred trainers">
          <PreferenceChips items={data.trainers} getLabel={(t) => t.trainerName} />
        </PreferenceSection>
        <PreferenceSection label="Preferred locations">
          <PreferenceChips items={data.locations} getLabel={(l) => l.locationName} />
        </PreferenceSection>
        <PreferenceSection label="Preferred session length">
          <PreferenceChips items={data.durations} getLabel={(d) => `${d} min`} />
        </PreferenceSection>
        <PreferenceSection label="Preferred time">
          {timeLines.length ? (
            timeLines.map((line) => (
              <Typography key={line.label} variant="body2" sx={{ mb: 0.25, fontSize: "0.875rem" }}>
                <strong>{line.label}:</strong>{" "}
                <Box component="span" sx={{ color: "primary.main", fontWeight: 500 }}>
                  {line.value}
                </Box>
              </Typography>
            ))
          ) : (
            <Typography variant="body1" color="textSecondary">
              Not set
            </Typography>
          )}
        </PreferenceSection>
        <PreferenceSection label="Date of birth">
          <Typography variant="body2" fontWeight={500} sx={{ fontSize: "0.875rem" }} color={dob ? "primary.main" : "textSecondary"}>
            {dob || "Not set"}
          </Typography>
        </PreferenceSection>
        <PreferenceSection label="Club offers & updates">
          {/* Pure opt-out: subscribed unless explicitly opted out ("unknown" still receives).
              One consent for both channels: WhatsApp offers and app-notification offers. */}
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{ fontSize: "0.875rem" }}
            color={data.whatsappConsent !== "opted_out" ? "primary.main" : "textSecondary"}
          >
            {data.whatsappConsent !== "opted_out" ? "Subscribed" : "Not subscribed"}
          </Typography>
        </PreferenceSection>
      </Box>
    </Box>
  );
}

export default PreferencesSummary;
